import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  BillItem,
  PatientBill,
  isBillDueDate,
  priceBillItems,
} from '../../models/patient-bill.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { FirebaseAuthService } from '../auth/firebase-auth.service';
import { MembershipService } from '../auth/membership.service';
import { BillingProductService } from './billing-product.service';
import { PatientBalanceService } from './patient-balance.service';

@Injectable({ providedIn: 'root' })
export class PatientBillService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private products = inject(BillingProductService);
  private balances = inject(PatientBalanceService);

  async listBills(patientId: string): Promise<PatientBill[]> {
    const snap = await getDocs(
      query(this.billsCollection(patientId), orderBy('createdAt', 'desc'))
    );
    return snap.docs
      .map((billDoc) => parsePatientBill(billDoc.id, billDoc.data()))
      .filter((bill): bill is PatientBill => bill != null);
  }

  async createBill(input: {
    patientId: string;
    patientName: string;
    doctorUid: string;
    doctorName: string;
    dueDate: string;
    items: BillItem[];
  }): Promise<string> {
    const uid = this.auth.uniqueId();
    if (!uid) {
      throw new Error('Not signed in');
    }
    this.assertCanBillFor(input.doctorUid);

    const patientName = input.patientName.trim();
    if (!patientName) {
      throw new Error('El paciente es obligatorio.');
    }
    const doctorName = input.doctorName.trim();
    if (!input.doctorUid || !doctorName) {
      throw new Error('Debe seleccionar un doctor.');
    }
    if (!isBillDueDate(input.dueDate)) {
      throw new Error('La fecha de vencimiento no es válida.');
    }
    const items = priceBillItems(input.items, await this.products.listProducts());
    if (items.length === 0) {
      throw new Error('Agregue al menos un producto.');
    }

    const billRef = doc(this.billsCollection(input.patientId));
    await setDoc(billRef, {
      patientId: input.patientId,
      patientName,
      doctorUid: input.doctorUid,
      doctorName,
      dueDate: input.dueDate,
      items,
      createdBy: uid,
      createdAt: serverTimestamp(),
    });
    await this.syncAmountOwed(input.patientId);
    return billRef.id;
  }

  async deleteBill(patientId: string, bill: PatientBill): Promise<void> {
    if (!this.canDeleteBill(bill)) {
      throw new Error('No tiene permiso para eliminar esta factura.');
    }
    await deleteDoc(this.billDoc(patientId, bill.id));
    await this.syncAmountOwed(patientId);
  }

  canDeleteBill(bill: Pick<PatientBill, 'doctorUid'>): boolean {
    const uid = this.auth.uniqueId();
    if (!uid) {
      return false;
    }
    if (this.membership.isSelectedBusinessSecretary()) {
      return true;
    }
    return this.membership.canAccessAppointments() && bill.doctorUid === uid;
  }

  private assertCanBillFor(doctorUid: string): void {
    const uid = this.auth.uniqueId();
    if (this.membership.isSelectedBusinessSecretary()) {
      return;
    }
    if (this.membership.canAccessAppointments() && doctorUid === uid) {
      return;
    }
    throw new Error('No tiene permiso para facturar a nombre de este doctor.');
  }

  private async syncAmountOwed(patientId: string): Promise<void> {
    try {
      await this.balances.recomputeAmountOwed(patientId);
    } catch (err) {
      console.error('[PatientBill] balance sync failed', err);
    }
  }

  private billsCollection(patientId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return collection(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'bills'
    );
  }

  private billDoc(patientId: string, billId: string) {
    const businessId = this.membership.requireSelectedBusinessId();
    return doc(
      this.firestore,
      'businesses',
      businessId,
      'patients',
      patientId,
      'bills',
      billId
    );
  }
}

function parsePatientBill(id: string, data: Record<string, unknown>): PatientBill | null {
  const patientId = typeof data['patientId'] === 'string' ? data['patientId'] : '';
  const patientName = typeof data['patientName'] === 'string' ? data['patientName'] : '';
  const doctorUid = typeof data['doctorUid'] === 'string' ? data['doctorUid'] : '';
  const doctorName = typeof data['doctorName'] === 'string' ? data['doctorName'] : '';
  const dueDate = typeof data['dueDate'] === 'string' ? data['dueDate'] : '';
  const createdBy = typeof data['createdBy'] === 'string' ? data['createdBy'] : '';
  const items = parseItems(data['items']);
  if (!patientId || !doctorUid || !dueDate || items.length === 0) {
    return null;
  }
  return {
    id,
    patientId,
    patientName,
    doctorUid,
    doctorName,
    dueDate,
    items,
    createdBy,
  };
}

function parseItems(value: unknown): BillItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const items: BillItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const productId = typeof record['productId'] === 'string' ? record['productId'].trim() : '';
    const name = typeof record['name'] === 'string' ? record['name'].trim() : '';
    const quantity = typeof record['quantity'] === 'number' ? record['quantity'] : 0;
    const unitPrice = typeof record['unitPrice'] === 'number' ? record['unitPrice'] : NaN;
    if (productId && name && quantity >= 1 && Number.isFinite(unitPrice) && unitPrice >= 0) {
      items.push({ productId, name, quantity, unitPrice });
    }
  }
  return items;
}
